import type {
  ShopifyAnalyticsContext,
  ShopifyContext,
} from "@intelligems/ig-embedded-preview/messages";
import { ComponentConfigType } from "@intelligems/ig-types/dtos/components";
import { EmbeddedNodeMap } from "@intelligems/ig-types/schemas/plugin/plugin/embedded";
import { WidgetDeviceType } from "@intelligems/ig-types/schemas/widgets/common";
import { createSlice } from "@reduxjs/toolkit";
import { extractPathname } from "@src/store/slices/experienceBuilder/common/utils/extractPathname";
import {
  CachedPageHtml,
  EmbeddedMod,
  ExperienceBuilderMode,
  ExperienceBuilderStats,
  IExperienceBuilderState,
  PluginConfigState,
} from "@src/store/slices/experienceBuilder/types";
import { ProxyLoadState } from "@src/components/ReverseProxy/ProxyStateController";

const initialState: IExperienceBuilderState = {
  targetUrl: "",
  lastLocation: "",
  demoUrl: null,
  mode: "navigate",
  device: "mobile",
  variationId: "",
  mods: [],
  initialMods: [],
  hiddenModIds: {},
  embeddedNodes: {},
  controlGroupLocked: true,
  openedAsOnsiteUpsell: false,
  initialComponentType: null,
  isDeploying: false,
  hasAutoDeployedPluginMismatch: false,
  cacheKey: "",
  dismissedPageState: {},
  pageHtmlUnavailable: false,
  cachedPageHtml: null,
  stats: {
    navigateCount: 0,
    startSessionTs: 0,
    startLoadTs: 0,
    loadTimes: [],
  },
};

export const experienceBuilderSlice = createSlice({
  name: "experienceBuilder",
  initialState,
  reducers: (create) => ({
    clear: create.reducer(() => initialState),
    initBuilder: create.reducer<{
      mods: EmbeddedMod[];
      variationId: string;
      targetUrl: string;
      cacheKey: string;
      demoUrl: string | null;
      isOnsiteUpsell: boolean;
      initialComponentType?: ComponentConfigType;
    }>((_, action) => {
      return {
        ...initialState,
        variationId: action.payload.variationId,
        mods: action.payload.mods,
        initialMods: action.payload.mods,
        targetUrl: action.payload.targetUrl,
        location: action.payload.targetUrl,
        lastLocation: action.payload.targetUrl,
        cacheKey: action.payload.cacheKey,
        demoUrl: action.payload.demoUrl,
        openedAsOnsiteUpsell: action.payload.isOnsiteUpsell,
        initialComponentType: action.payload.initialComponentType ?? null,
      };
    }),
    _setMode: create.reducer<ExperienceBuilderMode>((state, action) => {
      state.mode = action.payload;
    }),
    setDevice: create.reducer<WidgetDeviceType>((state, action) => {
      state.device = action.payload;
    }),
    _setTargetUrl: create.reducer<string>((state, action) => {
      state.targetUrl = action.payload;
      state.location = action.payload;
      state.lastLocation = action.payload;
    }),
    _setDemoUrl: create.reducer<string | null>((state, action) => {
      state.demoUrl = action.payload;
    }),
    setVariationId: create.reducer<string>((state, action) => {
      state.variationId = action.payload;
    }),
    setTargetThemeId: create.reducer<number | undefined>((state, action) => {
      state.targetThemeId = action.payload;
    }),
    setMods: create.reducer<EmbeddedMod[]>((state, action) => {
      state.mods = action.payload;
      // Mods just changed → the cached HTML no longer reflects what the
      // iframe is about to render. Drop it so the next `requestPageHtml`
      // either gets a fresh capture or fails loudly.
      state.cachedPageHtml = null;
    }),
    updateMods: create.reducer<{
      set?: EmbeddedMod[];
      remove?: string[];
    }>((state, action) => {
      const { set, remove } = action.payload;
      if (remove?.length) {
        const removeSet = new Set(remove);
        state.mods = state.mods.filter((m) => !removeSet.has(m.id));
      }
      if (set?.length) {
        for (const mod of set) {
          const index = state.mods.findIndex((m) => m.id === mod.id);
          if (index > -1) {
            state.mods[index] = mod;
          } else {
            state.mods.push(mod);
          }
        }
      }
      // See `setMods`: any mods mutation invalidates the cached HTML.
      state.cachedPageHtml = null;
    }),
    // The single writer for the hidden set, for one mod or a whole variation.
    //
    // Hiding is view-only and merchant-only: it never touches `mods`, so the
    // saved payload and the unsaved-changes comparison are unaffected by
    // construction.
    //
    // Absolute (`hidden`) rather than a toggle, and it takes the ids to act on
    // rather than deriving them. Both are for the same reason: only the caller
    // knows which variation is previewed, so only the caller can resolve a
    // toggle's direction from the variation-scoped hidden set without touching
    // ids belonging to another variation. Dispatch `setModsHidden` instead —
    // it tracks the action, and its callers derive direction from what they
    // rendered.
    _setModsHidden: create.reducer<{
      modificationIds: string[];
      hidden: boolean;
    }>((state, action) => {
      for (const id of action.payload.modificationIds) {
        if (action.payload.hidden) {
          state.hiddenModIds[id] = true;
        } else {
          delete state.hiddenModIds[id];
        }
      }
    }),
    setControlGroupLocked: create.reducer<boolean>((state, action) => {
      state.controlGroupLocked = action.payload;
    }),
    setEmbeddedNodes: create.reducer<EmbeddedNodeMap>((state, action) => {
      state.embeddedNodes = { ...state.embeddedNodes, ...action.payload };
    }),
    hoverMod: create.reducer<string>((state, action) => {
      state.highlightModId = action.payload;
    }),
    dwellMod: create.reducer<string>((state, action) => {
      state.focusModId = action.payload;
    }),
    blurMod: create.reducer((state) => {
      state.highlightModId = undefined;
      state.focusModId = undefined;
    }),
    setShopifyContext: create.reducer<ShopifyContext | undefined>(
      (state, action) => {
        state.shopifyContext = action.payload;
      }
    ),
    setLoadState: create.reducer<ProxyLoadState | undefined>(
      (state, action) => {
        state.loadState = action.payload;
        if (action.payload === undefined) {
          state.dismissedPageState = {};
        }
        // Any transition out of "ready" means the iframe is mid-reload or
        // erroring — the cached snapshot no longer reflects what the iframe
        // is showing. Drop it so the next capture either gets a fresh page
        // or surfaces the unavailable page-state.
        if (action.payload?.type !== "ready") {
          state.cachedPageHtml = null;
        }
      }
    ),
    setLocation: create.reducer<string | undefined | null>((state, action) => {
      state.location = action.payload;
      if (action.payload) {
        state.lastLocation = action.payload;
      }
    }),
    setPluginVersion: create.reducer<string | undefined | null>(
      (state, action) => {
        state.pluginVersion = action.payload;
      }
    ),
    setPluginConfigState: create.reducer<PluginConfigState>((state, action) => {
      state.pluginConfigState = action.payload;
    }),
    setShopifyAnalyticsContext: create.reducer<
      ShopifyAnalyticsContext | undefined
    >((state, action) => {
      state.shopifyAnalyticsContext = action.payload;
    }),
    setOtherModsExpanded: create.reducer<boolean>((state, action) => {
      state.otherModsExpanded = action.payload;
    }),
    commitBuilderMods: create.reducer((state) => {
      state.initialMods = state.mods;
    }),
    setIsRevertingToLegacyEditor: create.reducer<boolean>((state, action) => {
      state.isRevertingToLegacyEditor = action.payload;
    }),
    setIsDeploying: create.reducer<boolean>((state, action) => {
      state.isDeploying = action.payload;
    }),
    setHasAutoDeployedPluginMismatch: create.reducer<boolean>(
      (state, action) => {
        state.hasAutoDeployedPluginMismatch = action.payload;
      }
    ),
    setCacheKey: create.reducer<string>((state, action) => {
      state.cacheKey = action.payload;
    }),
    dismissPageState: create.reducer<string>((state, action) => {
      state.dismissedPageState[action.payload] = true;
    }),
    setPageHtmlUnavailable: create.reducer<boolean>((state, action) => {
      state.pageHtmlUnavailable = action.payload;
    }),
    setCachedPageHtml: create.reducer<CachedPageHtml>((state, action) => {
      state.cachedPageHtml = action.payload;
    }),
    setStats: create.reducer<ExperienceBuilderStats>((state, action) => {
      state.stats = action.payload;
    }),
    // Reset the per-turn preview counters after they've been drained into a
    // chat request, so each turn reports only its own navigations/loads.
    resetTurnStats: create.reducer((state) => {
      state.stats.navigateCount = 0;
      state.stats.loadTimes = [];
    }),
  }),
  selectors: {
    getMods: (state) => state.mods,
    getInitialMods: (state) => state.initialMods,
    getHiddenModIds: (state) => state.hiddenModIds,
    getMode: (state) => state.mode,
    getTargetUrl: (state) => state.targetUrl,
    getDemoUrl: (state) => state.demoUrl,
    /**
     * The origin (`https://host`) of the configured demo URL, or `undefined`
     * when none is set. A returned value means Shopify data must be read from
     * this origin's public Ajax API; it doubles as the base URL for those calls.
     */
    getDemoOrigin: (state) => {
      if (!state.demoUrl) {
        return undefined;
      }
      try {
        return new URL(state.demoUrl).origin;
      } catch {
        return undefined;
      }
    },
    isAuthenticated: (state) => !state.demoUrl,
    getDevice: (state) => state.device,
    getVariationId: (state) => state.variationId,
    getOpenedAsOnsiteUpsell: (state) => state.openedAsOnsiteUpsell,
    getInitialComponentType: (state) => state.initialComponentType,
    getControlGroupLocked: (state) => state.controlGroupLocked,
    getEmbeddedNodes: (state) => state.embeddedNodes,
    getHighlightModId: (state) => state.highlightModId,
    getFocusModId: (state) => state.focusModId,
    getLoadState: (state) => state.loadState,
    getLocation: (state) => state.location,
    getLastLocation: (state) => state.lastLocation,
    getPluginVersion: (state) => state.pluginVersion,
    getPluginConfigState: (state) => state.pluginConfigState,
    getShopifyContext: (state) => state.shopifyContext,
    getShopifyAnalyticsContext: (state) => state.shopifyAnalyticsContext,
    areOtherModsExpanded: (state) => state.otherModsExpanded ?? false,
    getPath: (state) =>
      state.location ? extractPathname(state.location) : undefined,
    getLastPath: (state) =>
      state.lastLocation ? extractPathname(state.lastLocation) : undefined,
    getTargetThemeId: (state) => state.targetThemeId,
    isDeploying: (state) => state.isDeploying,
    hasAutoDeployedPluginMismatch: (state) =>
      state.hasAutoDeployedPluginMismatch,
    getCacheKey: (state) => state.cacheKey,
    getPageHtmlUnavailable: (state) => state.pageHtmlUnavailable,
    getCachedPageHtml: (state) => state.cachedPageHtml,
    getStats: (state) => state.stats,
    getIsRevertingToLegacyEditor: (state) =>
      state.isRevertingToLegacyEditor ?? false,
  },
});
